import {
  MutableRefObject,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  on,
  viewportHeight,
  viewportStableHeight
} from '@telegram-apps/sdk-react';

export type UseKeyboardLiftOptions = {
  barHeight?: number;
  delayMs?: number;
  lastMessageRef?: MutableRefObject<HTMLDivElement | null>;
  replyMessageHeight?: number;
  contentRef?: MutableRefObject<HTMLDivElement | null>;
};

export const scrollToElement = (
  el: MutableRefObject<HTMLDivElement | null>,
  behavior: ScrollBehavior | undefined = 'smooth'
) => {
  if (el.current) {
    el.current.scrollIntoView({ behavior });
  }
};

export function useKeyboardLift({
  barHeight = 56,
  delayMs = 350,
  lastMessageRef,
  replyMessageHeight = 0,
  contentRef
}: UseKeyboardLiftOptions = {}) {
  const stableHeight = useMemo(() => viewportStableHeight() || 732, []);

  const [dyn, setDyn] = useState(() => viewportHeight() || 732);
  const [vvh, setVvh] = useState<number | null>(() =>
    typeof window !== 'undefined' && window.visualViewport
      ? window.visualViewport.height
      : 732
  );

  const lastKnownOffsetRef = useRef<number>(0);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const focusTimeRef = useRef<number>(0);
  const previousPaddingRef = useRef<number>(barHeight + replyMessageHeight);
  const scrollTopBeforeFocusRef = useRef<number>(0);
  const isBlurringRef = useRef<boolean>(false);
  const previousReplyHeightRef = useRef<number>(replyMessageHeight);

  const liveHeight = dyn ?? vvh;
  const realOffset = Math.max(0, stableHeight - liveHeight - 10);

  const [offset, setOffset] = useState(0);
  const contentPaddingBottom = offset + barHeight + replyMessageHeight;

  useLayoutEffect(() => {
    const isOutsideInput = (target: EventTarget | null) => {
      const input = textAreaRef.current;
      if (!input) return false;
      if (!(target instanceof Node)) return true;
      return !input.contains(target);
    };

    const maybePreventEarlyOutsidePress = (e: Event) => {
      const elapsed = Date.now() - focusTimeRef.current;
      if (elapsed < delayMs && isOutsideInput(e.target)) {
        e.preventDefault();
        e.stopPropagation();

        // requestAnimationFrame(() => {
        //   textAreaRef.current?.focus();
        // });
      }
    };

    window.addEventListener('pointerdown', maybePreventEarlyOutsidePress, {
      capture: true
    });
    window.addEventListener('mousedown', maybePreventEarlyOutsidePress, {
      capture: true
    });
    window.addEventListener('touchstart', maybePreventEarlyOutsidePress, {
      capture: true,
      passive: false
    });

    return () => {
      window.removeEventListener(
        'pointerdown',
        maybePreventEarlyOutsidePress,
        true
      );
      window.removeEventListener(
        'mousedown',
        maybePreventEarlyOutsidePress,
        true
      );
      window.removeEventListener(
        'touchstart',
        maybePreventEarlyOutsidePress,
        true
      );
    };
  }, [delayMs]);

  // subscriptions on viewport
  useLayoutEffect(() => {
    lastMessageRef?.current?.scrollIntoView({ behavior: 'auto' });

    const unsub = on('viewport_changed', () => setDyn(viewportHeight()));
    const vv = window.visualViewport;
    const handleVv = () => setVvh(vv?.height ?? null);
    vv?.addEventListener('resize', handleVv, { passive: true });
    vv?.addEventListener('scroll', handleVv, { passive: true });
    return () => {
      unsub?.();
      vv?.removeEventListener('resize', handleVv);
      vv?.removeEventListener('scroll', handleVv);
    };
  }, []);

  // update offset with delay
  useLayoutEffect(() => {
    if (realOffset > 0) lastKnownOffsetRef.current = realOffset;
    if (offset === realOffset) return;

    setOffset(realOffset);
  }, [realOffset, delayMs]);

  // Handle reply message height changes separately to avoid jerks
  useLayoutEffect(() => {
    if (!contentRef?.current) return;

    const replyHeightDelta =
      replyMessageHeight - previousReplyHeightRef.current;

    if (replyHeightDelta !== 0) {
      const scrollContainer = contentRef.current;
      const currentScrollTop = scrollContainer.scrollTop;

      // Adjust scroll to compensate for reply block height change
      requestAnimationFrame(() => {
        if (!contentRef?.current) return;
        contentRef.current.scrollTop = currentScrollTop + replyHeightDelta;
      });

      previousReplyHeightRef.current = replyMessageHeight;
    }
  }, [replyMessageHeight]);

  // Scroll content when contentPaddingBottom changes (keyboard opens/closes)
  useLayoutEffect(() => {
    if (!contentRef?.current) return;

    const previousPadding = previousPaddingRef.current;
    const paddingDelta = contentPaddingBottom - previousPadding;

    // Only adjust scroll if padding actually changed
    if (paddingDelta !== 0) {
      // Skip automatic adjustment during blur - we handle it manually
      if (isBlurringRef.current) {
        previousPaddingRef.current = contentPaddingBottom;
        return;
      }

      const scrollContainer = contentRef.current;
      const currentScrollTop = scrollContainer.scrollTop;
      const currentScrollHeight = scrollContainer.scrollHeight;
      const currentClientHeight = scrollContainer.clientHeight;
      const currentMaxScroll = currentScrollHeight - currentClientHeight;
      const isAtBottom = currentMaxScroll - currentScrollTop < 50;

      // Use requestAnimationFrame to ensure DOM has updated with new padding
      requestAnimationFrame(() => {
        if (!contentRef?.current) return;
        const scrollContainer = contentRef.current;

        if (isAtBottom) {
          // If user was at the bottom, keep them at the bottom after padding change
          scrollContainer.scrollTop =
            scrollContainer.scrollHeight - scrollContainer.clientHeight;
        } else {
          // Otherwise, adjust scroll position by the change in padding to maintain visible content
          scrollContainer.scrollTop = currentScrollTop + paddingDelta;
        }
      });

      previousPaddingRef.current = contentPaddingBottom;
    }
  }, [contentPaddingBottom]);

  const onPointerDown = () => {};

  const onFocus = () => {
    focusTimeRef.current = Date.now();

    // const heuristic = Math.min(420, Math.round(stableHeight * 0.35));
    //
    // const anticipate =
    //   lastKnownOffsetRef.current > 0 ? lastKnownOffsetRef.current : heuristic;
    //
    // setOffset(anticipate);
  };

  const onBlur = () => {
    const elapsed = Date.now() - focusTimeRef.current;
    if (elapsed < delayMs) {
      requestAnimationFrame(() => textAreaRef.current?.focus());
      return;
    }

    // Mark that we're blurring to skip automatic scroll adjustment
    isBlurringRef.current = true;

    // Save current scroll position while keyboard is still open (last visible message above input)
    if (contentRef?.current) {
      const currentScrollTop = contentRef.current.scrollTop;
      const currentPadding = contentPaddingBottom;
      const closedPadding = barHeight + replyMessageHeight;
      const paddingDifference = currentPadding - closedPadding;

      // Adjust the saved scroll position to account for the padding that will be removed
      const targetScrollTop = currentScrollTop - paddingDifference;
      scrollTopBeforeFocusRef.current = targetScrollTop;

      // Set scroll immediately to prevent flash
      contentRef.current.scrollTop = targetScrollTop;
    }

    setOffset(0);
    if (lastKnownOffsetRef.current > 350) {
      lastKnownOffsetRef.current = 0;
    }

    // Keep the scroll position stable after DOM updates
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (contentRef?.current) {
          contentRef.current.scrollTop = scrollTopBeforeFocusRef.current;
        }
        // Reset blur flag after scroll is restored
        isBlurringRef.current = false;
      });
    });
  };

  return {
    offset,
    stableHeight,
    textAreaRef,
    contentPaddingBottom,
    inputHandlers: {
      onPointerDown,
      onFocus,
      onBlur
    }
  };
}
